import { ElementType } from "react"
import { DropzoneOptions, useDropzone } from "react-dropzone"
import styled, { PolymorphicComponentProps } from "styled-components"
import { UploadCloudIcon } from "../../icons/UploadCloud"
import { Typography } from "../../typography"

const DropzoneContainer = styled(Typography)
  .withConfig({
    shouldForwardProp: (prop) => prop !== "isDragActive",
  })
  .attrs<{
    isDragActive?: boolean
  }>(({ as, isDragActive }) => ({
    as: as ?? "div",
    isDragActive: isDragActive ?? false,
  }))`
  max-width: 27.5rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 1.5rem;
  background: ${({ theme, isDragActive }) => `${isDragActive ? theme.colors.white : theme.colors.grey.$100}`};
  border-width: 1px;
  border-style: dashed;
  border-color: ${({ theme, isDragActive }) => `${isDragActive ? theme.colors.grey.$600 : theme.colors.grey.$400}`};
  color: ${({ theme, isDragActive }) => `${isDragActive ? theme.colors.grey.$600 : theme.colors.grey.$500}`};
  border-radius: 0.5rem;
  cursor: pointer;
  & svg {
    min-width: 1.5rem;
    margin-right: 0.8rem;
  }
  &:hover {
    background: ${({ theme }) => theme.colors.white};
    border-color: ${({ theme }) => theme.colors.grey.$600};
    & > * {
      color: ${({ theme }) => theme.colors.grey.$600};
    }
  }
`

export type DropzoneProps<C extends ElementType> = PolymorphicComponentProps<
  "web",
  {
    dropzoneOption?: DropzoneOptions
    inputProps: PolymorphicComponentProps<"web", { ["data-testid"]?: string }, "input", "input">
  },
  C,
  C
>

export const DeprecatedDropzone = <C extends ElementType = "div">({
  children,
  dropzoneOption = {},
  inputProps,
  ...containerProps
}: DropzoneProps<C>) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...dropzoneOption,
    maxSize: dropzoneOption.maxSize ?? 52428800.0, // 50 Mo
  })

  return (
    <DropzoneContainer {...getRootProps({ isDragActive })} {...containerProps}>
      <input {...getInputProps()} {...inputProps} />
      <UploadCloudIcon />
      <div>{children}</div>
    </DropzoneContainer>
  )
}
